/* eslint-disable react-refresh/only-export-components -- context provider dan hook adalah satu API publik. */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiRequest, jsonBody } from "../lib/api";
import { normalizeSession, type SessionUser } from "../lib/types";

interface LoginInput {
  username: string;
  password: string;
}

interface SessionContextValue {
  user?: SessionUser;
  loading: boolean;
  refresh: () => Promise<void>;
  login: (input: LoginInput) => Promise<SessionUser>;
  logout: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SessionUser>();
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await apiRequest<unknown>("/api/auth/me");
      setUser(normalizeSession(payload));
    } catch {
      setUser(undefined);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => void refresh(), 0);
    return () => window.clearTimeout(timer);
  }, [refresh]);

  const login = async (input: LoginInput) => {
    const payload = await apiRequest<unknown>("/api/auth/login", {
      method: "POST",
      ...jsonBody(input),
    });
    const authenticated = normalizeSession(payload);
    setUser(authenticated);
    return authenticated;
  };

  const logout = async () => {
    try {
      await apiRequest<unknown>("/api/auth/logout", {
        method: "POST",
        ...jsonBody({}),
      });
    } finally {
      setUser(undefined);
      sessionStorage.clear();
    }
  };

  const value = useMemo<SessionContextValue>(
    () => ({ user, loading, refresh, login, logout }),
    [loading, refresh, user],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error("useSession harus digunakan di dalam SessionProvider");
  return context;
}
